import { View, SxProp } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { UserEarnOfferListResponseDataItem } from '@utx/utx-client';

import { UniButton } from '@/design-system/components/buttons';
import { ButtonSize } from '@/design-system/components/buttons/useButton.hook';
import { SkeletonView } from '@/design-system/components/cards';
import { TableColumn, UniTable } from '@/design-system/components/table/UniTable';
import { TextTitle } from '@/design-system/components/typography';
import { keyGenerator } from '@/utils/keyGenerator';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { RootStackRoutes } from 'types';
import { EarnStackRoutes } from '../../earn.navigator';
import { CurrenciesIcon } from '../CurrenciesIcon';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { CurrenciesContext } from '@/currencies/currency.context';
import { useContext } from 'react';

interface AllOffersWebListProps {
  offers: Array<UserEarnOfferListResponseDataItem>;
  loading?: boolean;
}

export const AllOffersWebList: React.FC<AllOffersWebListProps> = ({ offers, loading }) => {
  const { t } = useTranslation();
  const { getDecimalByAsset } = useContext(CurrenciesContext);

  if (loading) {
    return (
      <View sx={{ ...styles.container, gap: '$3' }}>
        <SkeletonView height={20} />
        <SkeletonView height={20} width={200} />
        <SkeletonView height={20} width={150} />
      </View>
    );
  }

  const navigation = useNavigation();

  function onSubscribe(offerId: string) {
    navigation.navigate(RootStackRoutes.Earn, {
      screen: EarnStackRoutes.Subscribe,
      params: {
        offerId
      }
    });
  }

  const column: TableColumn<UserEarnOfferListResponseDataItem> = [
    [
      {
        title: '',
        index: 'title',
        renderTitle: (item) => <CurrenciesIcon asset={item.asset} textSize="H6" />,
        showTitleOnWrap: true,
        hideColumnTitleOnWrap: true,
        flex: 1
      }
    ],
    [
      {
        title: t('earn-invest::glossary.est-apy'),
        index: 'estimatedApy',
        textAlign: 'right',
        renderColumn: (item) => (
          <TextTitle bold success sx={{ textAlign: 'right', paddingX: '$3' }}>
            {formatAmountByDecimals({ value: item.estimatedApy, decimals: 2, suffix: '%' })}
          </TextTitle>
        ),
        columnSx: { fontWeight: 700, color: '$success' },
        flex: 0.6
      },
      {
        title: t('earn-invest::glossary.tenor'),
        index: 'tenor',
        formatValue: (item) => {
          const tenor = getEarnTenorTranslationKey(item.tenor);
          return t(tenor.key, tenor.options);
        },
        textAlign: 'right',
        columnSx: { fontWeight: 700 },
        flex: 0.6
      },
      {
        title: t('earn-invest::glossary.min-subscription-amount'),
        index: 'minSubscriptionAmount',
        formatValue: (item) =>
          formatAmountByDecimals({ value: item.minSubscriptionAmount, decimals: getDecimalByAsset(item.asset), suffix: item.asset }) ||
          ' -',
        textAlign: 'right',
        sx: {
          flex: 1
        }
      },
      {
        title: t('earn-invest::glossary.max-subscription-amount'),
        index: 'maxSubscriptionAmount',
        formatValue: (item) =>
          formatAmountByDecimals({ value: item.maxSubscriptionAmount, decimals: getDecimalByAsset(item.asset), suffix: item.asset }) ||
          ' - ',
        textAlign: 'right',
        flex: 1
      }
    ],
    [
      {
        title: '',
        index: 'id',
        textAlign: 'right',
        renderColumn: (item) => (
          <UniButton
            label={t('earn-invest::glossary.subscribe')}
            size={ButtonSize.medium}
            onPress={() => onSubscribe(item.id)}
            sx={{ marginX: '$3', width: 160 }}
          />
        ),
        sx: { width: 190, flexDirection: 'row', justifyContent: 'flex-end' }
      }
    ]
  ];

  const parsedData = offers.reduce((acc, item) => {
    const asset = item.asset;
    if (acc[asset]) {
      acc[asset].push(item);
    } else {
      acc[asset] = [item];
    }
    return acc;
  }, {} as Record<string, Array<UserEarnOfferListResponseDataItem>>);

  const parsedDataArr = Object.values(parsedData);

  return (
    <View sx={styles.container}>
      {parsedDataArr.map((item) => (
        <UniTable
          key={keyGenerator('all-offers-table')}
          data={item}
          column={column}
          wrapperSx={{ overflowX: 'auto', zIndex: 10 }}
          width={1000}
          onRowPressed={(item) => onSubscribe(item.id)}
        />
      ))}
    </View>
  );
};

enum StyleName {
  container = 'container'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    height: '100%',
    overflowX: 'auto'
  }
};
